import { Link } from "@tanstack/react-router";
import { Card, Tag } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
	ABSENCE_STATUS_LABELS,
	ABSENCE_TYPE_LABELS,
	type AbsenceStatus,
	type AbsenceType,
} from "@/modules/absences/types";
import { usePermission } from "@/shared/hooks/usePermission";
import { useRecentAbsences } from "../hooks/useEmployeeProfile";

/** `YYYY-MM-DD` → `DD.MM` (kanvas shakli — yil qatorni uzaytirardi). */
function qisqaSana(qiymat: string): string {
	return dayjs(qiymat).format("DD.MM");
}

/**
 * «Oxirgi arizalar» yon kartasi (kanvas, o'ng ustun).
 *
 * ⚠️ **Yorliq lug'atlari qo'shni moduldan IMPORT qilinadi, HOOK
 * emas** (`employeeTabs.tsx` naqshi): lug'atni nusxalash ikki ro'yxatni
 * jimgina ajratardi — bir joyda «Yillik ta'til», boshqasida
 * `undefined`.
 *
 * ⚠️⚠️ **Tartib SERVERDA, klientda qayta tartiblanmaydi** — sabab
 * `useRecentAbsences` izohida.
 *
 * ⚠️ Ruxsat yo'q bo'lsa karta UMUMAN chizilmaydi (hook ham `enabled:
 * false` bilan so'rov yubormaydi) — ko'rinadigan, lekin doim bo'sh
 * karta foydalanuvchiga «arizasi yo'q» degan YOLG'ON aytardi.
 *
 * ⚠️ «Barchasi →» havolasi RAQAMSIZ: sanoq endpointi yo'q va qo'shimcha
 * so'rov `destroyOnHidden` qarorining asosini bekor qilardi.
 */
export function RecentAbsencesCard({ employeeId }: { employeeId: string }) {
	const { t } = useTranslation("employees");
	const { can } = usePermission();
	const { data = [] } = useRecentAbsences(employeeId);

	if (!can("absences", "read")) {
		return null;
	}

	return (
		<Card
			title={t("recentAbsencesCard.title")}
			extra={
				// ⚠️ `search` FUNKSIYA shaklida — `/employees/$id` marshrutida
				// `validateSearch` yo'q, ya'ni obyekt literali qat'iy tipga
				// urilardi (`as never` esa taqiqlangan).
				<Link
					to="."
					search={(eski: Record<string, unknown>) => ({ ...eski, tab: "arizalar" })}
					className="text-[#1c5cab] hover:underline"
				>
					{t("recentAbsencesCard.allLink")}
				</Link>
			}
		>
			{data.length === 0 ? (
				<div className="text-xs text-[#8a929e]">{t("recentAbsencesCard.empty")}</div>
			) : (
				data.map((ariza) => {
					const holat = ABSENCE_STATUS_LABELS[ariza.status as AbsenceStatus];

					return (
						<div
							key={ariza.id}
							data-testid="absence-row"
							className="flex items-baseline justify-between gap-2 border-b border-[#eeefec] py-2 text-sm last:border-b-0"
						>
							<span className="min-w-0 truncate">
								{ABSENCE_TYPE_LABELS[ariza.type as AbsenceType] ?? ariza.type}
								<span className="ml-1 text-xs text-[#8a929e]">
									{`· ${qisqaSana(ariza.dateFrom)} – ${qisqaSana(ariza.dateTo)}`}
								</span>
							</span>

							{holat ? <Tag color={holat.color}>{holat.label}</Tag> : null}
						</div>
					);
				})
			)}
		</Card>
	);
}
